import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { devFilesApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';
import {
  RefreshCw, Plus, Trash2, X, Save, Pencil, History,
  ChevronLeft, ChevronRight, Search, GripVertical,
  List, Table, FilterX
} from 'lucide-react';

const TYPE_OPTIONS = ['minor', 'major'];

const RevLogPage = () => {
  // ── Shared ────────────────────────────────────────────────────────────────
  const [revLog, setRevLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const { notify } = useNotifications();

  // ── List view ─────────────────────────────────────────────────────────────
  const [selectedRevision, setSelectedRevision] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [listCollapsed, setListCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // ── Add revision modal ────────────────────────────────────────────────────
  const [showAddRevision, setShowAddRevision] = useState(false);
  const [newRevision, setNewRevision] = useState({ type: 'minor', summary: '', changes: [''] });

  // ── Datasheet state ───────────────────────────────────────────────────────
  const [dsFilters, setDsFilters] = useState({});
  const [dsEditMode, setDsEditMode] = useState(false);
  const [dsEdits, setDsEdits] = useState({});
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [changesModalRev, setChangesModalRev] = useState(null);
  const [changesLocal, setChangesLocal] = useState([]);

  // ── Data loading ──────────────────────────────────────────────────────────
  const loadRevLog = useCallback(async () => {
    try {
      setLoading(true);
      const response = await devFilesApi.getRevLog();
      setRevLog(response.data);
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to load revision log');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadRevLog(); }, [loadRevLog]);

  // Auto-select first revision on load; re-sync when revLog updates
  useEffect(() => {
    if (!revLog?.revisions) return;
    setSelectedRevision(prev => {
      if (prev) {
        const updated = revLog.revisions.find(r => r.version === prev.version);
        return updated || revLog.revisions[0] || null;
      }
      return revLog.revisions[0] || null;
    });
  }, [revLog]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getTypeClass = (type) => type === 'major' ? 'type-major' : 'type-minor';

  const hasChanges = isEditing && editForm && selectedRevision && (
    editForm.type !== selectedRevision.type ||
    editForm.summary !== selectedRevision.summary ||
    JSON.stringify(editForm.changes) !== JSON.stringify(selectedRevision.changes)
  );

  // ── Filtered data ─────────────────────────────────────────────────────────
  const filteredRevisions = useMemo(() => {
    if (!revLog?.revisions) return [];
    let result = [...revLog.revisions];
    Object.entries(dsFilters).forEach(([key, vals]) => {
      if (vals?.length > 0) result = result.filter(r => vals.includes(r[key] || ''));
    });
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(r =>
        r.version?.toLowerCase().includes(term) ||
        r.summary?.toLowerCase().includes(term) ||
        r.type?.toLowerCase().includes(term)
      );
    }
    return result;
  }, [revLog, dsFilters, searchTerm]);

  const dsDisplayData = useMemo(() => filteredRevisions, [filteredRevisions]);

  const dsIsDirty = Object.keys(dsEdits).length > 0;
  const dsHasFilters = Object.values(dsFilters).some(f => f?.length > 0);
  const dsFilteredCount = useMemo(() => {
    if (!revLog?.revisions) return 0;
    return revLog.revisions.filter(row =>
      Object.entries(dsFilters).every(([k, vals]) => !vals || vals.length === 0 || vals.includes(String(row[k] || '')))
    ).length;
  }, [revLog, dsFilters]);

  const dsColumns = useMemo(() => [
    { key: 'version',  fieldName: 'version',  label: 'Version', width: 100 },
    { key: 'type',     fieldName: 'type',     label: 'Type',    width: 100,
      formatter: (v) => v ? <span className={`rev-type-badge ${getTypeClass(v)}`}>{v === 'major' ? 'MAJOR' : 'MINOR'}</span> : null },
    { key: 'date',     fieldName: 'date',     label: 'Date',    width: 120 },
    { key: 'summary',  fieldName: 'summary',  label: 'Summary', width: 600 },
  ], []); // eslint-disable-line react-hooks/exhaustive-deps

  const renderDsCell = (row, col, displayValue) => {
    switch (col.key) {
      case 'type':
        return (
          <select className="ds-cell-input" value={displayValue ?? 'minor'} onChange={e => handleDsEdit(row.id, 'type', e.target.value)}>
            {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t === 'major' ? 'Major' : 'Minor'}</option>)}
          </select>
        );
      case 'summary':
        return <input className="ds-cell-input" value={displayValue ?? ''} onChange={e => handleDsEdit(row.id, 'summary', e.target.value)} style={{ width: '100%' }} />;
      default:
        return displayValue;
    }
  };

  // ── List view handlers ────────────────────────────────────────────────────
  const handleSelectRevision = (revision) => {
    if (isEditing) {
      notify.confirm('Discard unsaved changes?', () => { cancelEditing(); setSelectedRevision(revision); });
      return;
    }
    setSelectedRevision(revision);
  };

  const startEditing = () => {
    if (!selectedRevision) return;
    setEditForm({ ...selectedRevision, changes: [...selectedRevision.changes] });
    setIsEditing(true);
  };

  const cancelEditing = () => { setIsEditing(false); setEditForm({}); };

  const handleSave = async () => {
    try {
      setSaving(true);
      await devFilesApi.updateRevision(selectedRevision.id, editForm);
      setIsEditing(false);
      setEditForm({});
      await loadRevLog();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to save revision');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRevision = async () => {
    if (!newRevision.summary.trim()) return;
    const filteredChanges = newRevision.changes.filter(c => c.trim());
    if (filteredChanges.length === 0) return;
    try {
      setSaving(true);
      await devFilesApi.addRevision({ type: newRevision.type, summary: newRevision.summary, changes: filteredChanges });
      setNewRevision({ type: 'minor', summary: '', changes: [''] });
      setShowAddRevision(false);
      await loadRevLog();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to add revision');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRevision = () => {
    if (!selectedRevision) return;
    if (revLog.revisions.length === 1) { notify.error('Cannot delete the only revision'); return; }
    notify.confirm(`Delete revision ${selectedRevision.version}?`, async () => {
      try {
        setSaving(true);
        await devFilesApi.deleteRevision(selectedRevision.id);
        setIsEditing(false);
        setSelectedRevision(null);
        await loadRevLog();
      } catch (err) {
        notify.error(err.response?.data?.message || 'Failed to delete revision');
      } finally {
        setSaving(false);
      }
    });
  };

  const addChangeLine = (isEdit = false) => {
    if (isEdit) setEditForm({ ...editForm, changes: [...editForm.changes, ''] });
    else setNewRevision({ ...newRevision, changes: [...newRevision.changes, ''] });
  };

  const updateChangeLine = (index, value, isEdit = false) => {
    if (isEdit) {
      const c = [...editForm.changes]; c[index] = value; setEditForm({ ...editForm, changes: c });
    } else {
      const c = [...newRevision.changes]; c[index] = value; setNewRevision({ ...newRevision, changes: c });
    }
  };

  const removeChangeLine = (index, isEdit = false) => {
    if (isEdit) {
      const c = editForm.changes.filter((_, i) => i !== index);
      setEditForm({ ...editForm, changes: c.length ? c : [''] });
    } else {
      const c = newRevision.changes.filter((_, i) => i !== index);
      setNewRevision({ ...newRevision, changes: c.length ? c : [''] });
    }
  };

  // ── Datasheet handlers ────────────────────────────────────────────────────
  const handleDsEdit = (id, field, value) => {
    setDsEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const handleDsCancel = () => { setDsEdits({}); setDsEditMode(false); };

  const handleDsSave = async () => {
    const dirtyIds = Object.keys(dsEdits);
    if (dirtyIds.length === 0) return;
    try {
      setSaving(true);
      for (const id of dirtyIds) {
        const original = revLog.revisions.find(r => r.id === parseInt(id));
        if (!original) continue;
        await devFilesApi.updateRevision(original.id, { ...original, ...dsEdits[id] });
      }
      setDsEdits({});
      setDsEditMode(false);
      notify.success('Changes saved');
      await loadRevLog();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const openChangesModal = (rev) => {
    const merged = { ...rev, ...(dsEdits[rev.id] || {}) };
    setChangesModalRev(rev);
    setChangesLocal([...(merged.changes || [])]);
    setShowChangesModal(true);
  };

  const handleChangesApply = () => {
    if (!changesModalRev) return;
    const filtered = changesLocal.filter(c => c.trim());
    setDsEdits(prev => ({ ...prev, [changesModalRev.id]: { ...(prev[changesModalRev.id] || {}), changes: filtered.length ? filtered : [''] } }));
    setShowChangesModal(false);
  };

  if (loading) return <div className="dev-page loading">Loading...</div>;

  return (
    <div className="module-container">

      {/* ── Header ── */}
      <div className="module-header">
        <div className="module-header-left">
          <History size={20} style={{ color: 'var(--color-primary)' }} />
          <h1>Rev Log</h1>
          <div className="module-header-tabs">
            <button onClick={() => setViewMode('list')} className={`tab-btn-icon ${viewMode === 'list' ? 'active' : ''}`} title="List View"><List size={18} /></button>
            <button onClick={() => setViewMode('datasheet')} className={`tab-btn-icon ${viewMode === 'datasheet' ? 'active' : ''}`} title="Datasheet View"><Table size={18} /></button>
          </div>
          <span className="module-header-meta">
            v{revLog?.currentVersion} &bull; {revLog?.revisions.length} revision{revLog?.revisions.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="module-header-actions">

          {/* List view CRUD */}
          {viewMode === 'list' && (
            <>
              {isEditing
                ? <button className="crud-btn" onClick={handleDeleteRevision} disabled={saving || revLog?.revisions.length === 1} title="Delete" style={{ color: 'var(--color-error)' }}><Trash2 size={18} /></button>
                : <button className="crud-btn" onClick={() => setShowAddRevision(true)} title="Add Revision"><Plus size={18} /></button>
              }
              <button className={`crud-btn ${!selectedRevision && !isEditing ? 'disabled' : ''}`} onClick={isEditing ? cancelEditing : startEditing} disabled={!selectedRevision && !isEditing} title={isEditing ? 'Cancel' : 'Edit'}>
                {isEditing ? <X size={18} /> : <Pencil size={18} />}
              </button>
              <button className={`crud-btn ${!isEditing || !hasChanges ? 'disabled' : ''}`} onClick={handleSave} disabled={!isEditing || saving || !hasChanges} title="Save"><Save size={18} /></button>
            </>
          )}

          {/* Datasheet controls */}
          {viewMode === 'datasheet' && (
            <>
              <button className="crud-btn" onClick={dsEditMode ? handleDsCancel : () => setDsEditMode(true)} title={dsEditMode ? 'Cancel' : 'Edit'}>
                {dsEditMode ? <X size={18} /> : <Pencil size={18} />}
              </button>
              <button className={`crud-btn ${!dsIsDirty || saving ? 'disabled' : ''}`} onClick={handleDsSave} disabled={!dsIsDirty || saving} title="Save"><Save size={18} /></button>
            </>
          )}

          {/* Clear filter */}
          {dsHasFilters && (
            <button onClick={() => setDsFilters({})} className="clear-filter-btn" title="Clear filters">
              <FilterX size={16} /><span>{dsFilteredCount}/{revLog?.revisions.length || 0}</span>
            </button>
          )}

          {/* Search */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search size={14} style={{ position: 'absolute', left: '8px', color: 'var(--color-gray-400)', pointerEvents: 'none' }} />
            <input
              className="form-input"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search revisions..."
              style={{ paddingLeft: '28px', width: '180px', fontSize: 'var(--text-sm)' }}
            />
          </div>

          {/* 6-dot */}
          <div className="action-menu" ref={menuRef}>
            <button className="action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="action-menu-dropdown" style={{ minWidth: '160px' }}>
                <button className="action-menu-item" onClick={() => { setMenuOpen(false); loadRevLog(); }}><RefreshCw size={16} /><span>Refresh</span></button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="list-view">

        {/* ── List view ── */}
        {viewMode === 'list' && (
          <>
            {listCollapsed ? (
              <div className="list-panel-collapsed">
                <button onClick={() => setListCollapsed(false)} className="list-panel-expand-btn" title="Expand"><ChevronRight size={16} /></button>
              </div>
            ) : (
              <div className="list-panel">
                <div className="list-panel-header">
                  <button onClick={() => setListCollapsed(true)} className="list-panel-collapse-btn" title="Collapse"><ChevronLeft size={16} /></button>
                </div>
                <div className="list-scroll">
                  {filteredRevisions.length > 0 ? filteredRevisions.map(revision => (
                    <button key={revision.version} onClick={() => handleSelectRevision(revision)} className={`list-item-btn ${selectedRevision?.version === revision.version ? 'selected' : ''}`}>
                      <span className="list-item-primary">
                        <span className={`rev-type-badge ${getTypeClass(revision.type)}`} style={{ marginRight: '6px', fontSize: '9px', padding: '1px 4px', borderRadius: '8px' }}>
                          {revision.type === 'major' ? 'MAJ' : 'MIN'}
                        </span>
                        {revision.version}
                      </span>
                      <span className="list-item-secondary">{revision.date}</span>
                    </button>
                  )) : <div className="task-list-empty">No revisions</div>}
                </div>
              </div>
            )}

            {/* Detail panel */}
            <div className="detail-panel">
              {selectedRevision ? (
                <div className="detail-content" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

                  {/* General Information — fixed */}
                  <div style={{ flexShrink: 0 }}>
                    <div className="detail-grid-24">

                      <div className="col-span-24">
                        <div className="detail-section-header">General Information</div>
                      </div>

                      <div className="form-group col-span-6">
                        <label>Version</label>
                        <input type="text" className="form-input" value={selectedRevision.version} readOnly onChange={() => {}} />
                      </div>

                      <div className="form-group col-span-8">
                        <label>Type</label>
                        {isEditing
                          ? <select className="form-input" value={editForm.type} onChange={e => setEditForm({ ...editForm, type: e.target.value })}>
                              <option value="minor">Minor</option>
                              <option value="major">Major</option>
                            </select>
                          : <span className={`rev-type-badge ${getTypeClass(selectedRevision.type)}`}>{selectedRevision.type === 'major' ? 'MAJOR' : 'MINOR'}</span>
                        }
                      </div>

                      <div className="form-group col-span-10">
                        <label>Date</label>
                        <input type="text" className="form-input" value={selectedRevision.date || ''} readOnly onChange={() => {}} />
                      </div>

                      <div className="col-span-24">
                        <div className="detail-section-header">Summary</div>
                      </div>

                      <div className="form-group col-span-24">
                        {isEditing
                          ? <input type="text" className="form-input" value={editForm.summary} onChange={e => setEditForm({ ...editForm, summary: e.target.value })} autoFocus />
                          : <input type="text" className="form-input" value={selectedRevision.summary} readOnly onChange={() => {}} />
                        }
                      </div>

                    </div>
                  </div>

                  {/* Changes — fills remaining space */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <div className="detail-section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>Changes</span>
                      {isEditing && (
                        <button className="btn btn-secondary btn-sm" onClick={() => addChangeLine(true)} style={{ padding: '2px 8px', fontSize: 'var(--text-xs)' }}>
                          <Plus size={12} /> Add
                        </button>
                      )}
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                      {isEditing ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                          {editForm.changes.map((change, index) => (
                            <div key={index} style={{ display: 'flex', gap: 'var(--spacing-xs)', alignItems: 'center' }}>
                              <input type="text" className="form-input" value={change} onChange={e => updateChangeLine(index, e.target.value, true)} placeholder="Describe a change..." style={{ flex: 1 }} />
                              <button className="btn-icon-subtle" onClick={() => removeChangeLine(index, true)} disabled={editForm.changes.length === 1} title="Remove"><X size={16} /></button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <ul style={{ margin: 0, paddingLeft: 'var(--spacing-lg)', lineHeight: '1.7' }}>
                          {selectedRevision.changes.map((change, index) => (
                            <li key={index} style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-700)' }}>{change}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                </div>
              ) : (
                <div className="dev-detail-empty"><p>Select a revision to view details</p></div>
              )}
            </div>
          </>
        )}

        {/* ── Datasheet view ── */}
        {viewMode === 'datasheet' && (
          <div className="datasheet-view">
            <FilterableDataGrid
              data={dsDisplayData}
              allData={revLog?.revisions || []}
              columns={dsColumns}
              filters={dsFilters}
              setFilters={setDsFilters}
              editModeEnabled={dsEditMode}
              editedData={dsEdits}
              renderEditableCell={renderDsCell}
              onRowDoubleClick={(row) => openChangesModal(row)}
            />
          </div>
        )}
      </div>

      {/* ── Add Revision Modal ── */}
      {showAddRevision && (
        <div className="modal-overlay">
          <div className="modal-content dev-plan-modal">
            <div className="modal-header">
              <h3>New Revision</h3>
              <button onClick={() => setShowAddRevision(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="detail-grid-24" style={{ marginBottom: 0 }}>

                <div className="col-span-24">
                  <div className="detail-section-header" style={{ marginTop: 0 }}>General Information</div>
                </div>

                <div className="form-group col-span-8">
                  <label>Type</label>
                  <select className="form-input" value={newRevision.type} onChange={e => setNewRevision({ ...newRevision, type: e.target.value })}>
                    <option value="minor">Minor</option>
                    <option value="major">Major</option>
                  </select>
                </div>

                <div className="form-group col-span-16">
                  <label>Summary</label>
                  <input type="text" className="form-input" value={newRevision.summary} onChange={e => setNewRevision({ ...newRevision, summary: e.target.value })} placeholder="Brief description of this release" autoFocus />
                </div>

                <div className="col-span-24">
                  <div className="detail-section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Changes</span>
                    <button className="btn btn-secondary btn-sm" onClick={() => addChangeLine()} style={{ padding: '2px 8px', fontSize: 'var(--text-xs)' }}>
                      <Plus size={12} /> Add
                    </button>
                  </div>
                </div>

                <div className="col-span-24">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                    {newRevision.changes.map((change, index) => (
                      <div key={index} style={{ display: 'flex', gap: 'var(--spacing-xs)', alignItems: 'center' }}>
                        <input type="text" className="form-input" value={change} onChange={e => updateChangeLine(index, e.target.value)} placeholder="Describe a change..." style={{ flex: 1 }} />
                        <button className="btn-icon-subtle" onClick={() => removeChangeLine(index)} disabled={newRevision.changes.length === 1} title="Remove"><X size={16} /></button>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddRevision(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddRevision} disabled={saving || !newRevision.summary.trim() || !newRevision.changes.some(c => c.trim())}>
                {saving ? 'Adding...' : 'Add Revision'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Changes popup (datasheet double-click) ── */}
      {showChangesModal && changesModalRev && (
        <div className="modal-overlay">
          <div className="modal-content dev-plan-modal" style={{ maxWidth: '680px', height: '560px' }}>
            <div className="modal-header">
              <h3>Changes — {changesModalRev.version}</h3>
              <button onClick={() => setShowChangesModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
              {dsEditMode ? (
                <>
                  {changesLocal.map((change, index) => (
                    <div key={index} style={{ display: 'flex', gap: 'var(--spacing-xs)', alignItems: 'center' }}>
                      <input type="text" className="form-input" value={change} onChange={e => { const c = [...changesLocal]; c[index] = e.target.value; setChangesLocal(c); }} placeholder="Describe a change..." style={{ flex: 1 }} autoFocus={index === 0} />
                      <button className="btn-icon-subtle" onClick={() => setChangesLocal(prev => { const c = prev.filter((_, i) => i !== index); return c.length ? c : ['']; })} disabled={changesLocal.length === 1} title="Remove"><X size={16} /></button>
                    </div>
                  ))}
                  <button className="btn btn-secondary btn-sm" onClick={() => setChangesLocal(prev => [...prev, ''])} style={{ alignSelf: 'flex-start' }}>
                    <Plus size={12} /> Add
                  </button>
                </>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 'var(--spacing-lg)', lineHeight: '1.9' }}>
                  {changesLocal.map((change, index) => (
                    <li key={index} style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-700)' }}>{change}</li>
                  ))}
                </ul>
              )}
            </div>
            {dsEditMode && (
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowChangesModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleChangesApply}>Apply</button>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default RevLogPage;
